import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Num, Plural, T, useGT } from 'gt-react';
import type {
  WorkbookDefinition,
  WorkbookPivotSheet,
  WorkbookResult,
  WorkbookSource,
  WorkbookTable,
} from '@erp/shared-types';
import type { WorkbookPlan } from '@erp/workbooks';
import type { SheetPreview } from '../SheetEditor';
import { SheetNameField, SheetPanelHeading } from '../SheetPanelHeading';
import { WorkbookTable as ResultTable } from '../WorkbookTable';
import { outputTypes } from '../WorkbookCellMenu';
import { useWorkbookRunTime } from '../workbookRunTime';
import { PivotEditor } from './PivotEditor';
import { PivotValidations } from './PivotValidations';
import { useWorksheetResultView } from '../useWorksheetResultView';
import type { ChangeSheetResult } from '../worksheetValidations';
import {
  WorksheetResultControls,
  WorksheetResultSample,
} from '../WorksheetResultViews';
import { pivotPanelData, upstreamPreview } from './pivotFields';
import { pivotPercentColumns, totalRowBlank } from './pivotDisplay';
import { sourcePreviewQuery } from '../useSourcePreview';

export type PivotPanelProps = {
  workbookId: string;
  sheet: WorkbookPivotSheet;
  definition: WorkbookDefinition;
  plan: WorkbookPlan;
  result?: WorkbookResult;
  /** The Run of the shown Result; absent for a Live preview. */
  runId?: string;
  /** The input Worksheet's Result, when the input is a Worksheet. */
  inputResult?: WorkbookResult;
  stale: boolean;
  ranAt?: string;
  preview?: SheetPreview;
  needsRunHint: string;
  runMenu?: ReactNode;
  readOnly: boolean;
  change: (update: (sheet: WorkbookPivotSheet) => WorkbookPivotSheet) => void;
};

/**
 * A pivot Worksheet's tab. Its input's rows before a Run come from a
 * Source's preview (read from the server unless inline) or the input
 * Worksheet's Result.
 */
export function PivotPanel(props: PivotPanelProps) {
  const source = props.definition.sources.find(
    (item) => item.id === props.sheet.inputId,
  );
  if (source && source.kind !== 'input')
    return <RemotePivotPanel key={source.id} {...props} source={source} />;
  return (
    <PivotView
      {...props}
      input={
        source?.kind === 'input'
          ? source.table
          : (props.inputResult ??
            upstreamPreview(props.definition, props.sheet.inputId))
      }
    />
  );
}

function RemotePivotPanel({
  source,
  ...props
}: PivotPanelProps & { source: WorkbookSource }) {
  const query = useQuery(sourcePreviewQuery(props.workbookId, source));
  return <PivotView {...props} input={query.data} />;
}

/**
 * The pivot's Result as a table beside the pivot editor, after a
 * spreadsheet's pivot table and its editor panel.
 */
function PivotView({
  input,
  ...props
}: PivotPanelProps & { input?: WorkbookTable }) {
  const gt = useGT();
  const { sheet, definition, result } = props;
  const { fields, inputs, diagnostics, unfinished } = pivotPanelData({
    ...props,
    input,
  });
  // Validations and Samples write only the parts every kind of Worksheet has.
  const changeResult: ChangeSheetResult = (update) => props.change(update);
  const view = useWorksheetResultView({
    workbookId: props.workbookId,
    runId: props.runId,
    sheet,
    result,
    readOnly: props.readOnly,
    change: changeResult,
  });
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <SheetPanelHeading
        sheet={sheet}
        definition={definition}
        diagnostics={diagnostics}
        preview={props.preview}
        runMenu={props.runMenu}
      />
      <div className="flex min-w-0 items-start gap-3">
        <section
          className="min-w-0 flex-1 space-y-2"
          aria-label={gt('Pivot result', { context: 'pivot result section' })}
        >
          <WorksheetResultControls sheet={sheet} result={result} view={view} />
          <WorksheetResultSample sheet={sheet} result={result} view={view} />
          <PivotResultBody
            sheet={sheet}
            result={result}
            focus={view.focus}
            unfinished={unfinished}
            stale={props.stale}
            needsRunHint={props.needsRunHint}
          />
          {props.stale && result && (
            <PivotStaleNotice ranAt={props.ranAt} definition={definition} />
          )}
        </section>
        <aside
          className="w-80 shrink-0 rounded-xl border bg-card p-3"
          aria-label={gt('Pivot editor', { context: 'pivot editor panel' })}
        >
          <SheetNameField
            className="mb-3"
            name={sheet.name}
            readOnly={props.readOnly}
            rename={(name) => props.change((latest) => ({ ...latest, name }))}
          />
          <PivotEditor
            sheet={sheet}
            fields={fields}
            inputs={inputs}
            observedColumnKeys={result?.pivotColumnKeys}
            readOnly={props.readOnly}
            change={props.change}
          />
          <PivotValidations
            sheet={sheet}
            result={result}
            readOnly={props.readOnly}
            change={changeResult}
          />
        </aside>
      </div>
    </div>
  );
}

/** The pivot's Result, or what it needs first: a field, or a run. */
function PivotResultBody({
  sheet,
  result,
  focus,
  unfinished,
  stale,
  needsRunHint,
}: Pick<PivotPanelProps, 'sheet' | 'result' | 'stale' | 'needsRunHint'> & {
  focus: ReturnType<typeof useWorksheetResultView>['focus'];
  unfinished: boolean;
}) {
  if (unfinished)
    return (
      <p className="rounded-md border p-3 text-sm text-muted-foreground">
        <T context="pivot empty state">
          Add a field to Rows or Values to start. Drag one from Fields in the
          editor, or pick it from an area&apos;s Add menu.
        </T>
      </p>
    );
  if (!result)
    return (
      <p className="rounded-md border p-3 text-xs text-muted-foreground">
        {needsRunHint}
      </p>
    );
  return (
    <PivotResultTable
      sheet={sheet}
      result={focus ? { ...result, rows: [...focus.rows] } : result}
      rowNumbers={focus?.rowNumbers ?? result.sample?.rowNumbers}
      stale={stale}
    />
  );
}

/** A pivot's Result preview, its shown-as Values as percentages, with its row count. */
function PivotResultTable({
  sheet,
  result,
  stale,
  rowNumbers,
}: {
  sheet: WorkbookPivotSheet;
  result: WorkbookResult;
  /** Its spec changed since this Result: shown as computed, muted until a rerun. */
  stale: boolean;
  /** The shown rows' positions in the whole Result, when they are a Sample or a Validation's failing rows. */
  rowNumbers?: readonly number[];
}) {
  const partial = result.rows.length < result.rowCount;
  const rowFields = sheet.pivot.rows.length;
  return (
    <>
      <div
        data-stale={stale ? '' : undefined}
        className={stale ? 'opacity-60' : undefined}
      >
        <ResultTable
          table={{ columns: result.columns, rows: result.rows }}
          rowNumbers={rowNumbers}
          types={outputTypes(sheet, result.columns)}
          percentColumns={pivotPercentColumns(sheet, result)}
          groupThousands
          blankCell={(row, column) =>
            totalRowBlank(rowFields, result.rows[row] ?? [], column)
          }
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {partial ? (
          <T context="pivot result rows shown">
            Showing <Num>{result.rows.length}</Num> of{' '}
            <Num>{result.rowCount}</Num>{' '}
            <Plural n={result.rowCount} one="row" other="rows" />
          </T>
        ) : (
          <T context="pivot result rows">
            <Num>{result.rowCount}</Num>{' '}
            <Plural n={result.rowCount} one="row" other="rows" />
          </T>
        )}
      </p>
    </>
  );
}

function PivotStaleNotice({
  ranAt,
  definition,
}: {
  ranAt?: string;
  definition: WorkbookDefinition;
}) {
  const runTime = useWorkbookRunTime(definition);
  return (
    <p
      role="status"
      className="text-xs text-muted-foreground"
      title={ranAt ? runTime.full(ranAt) : undefined}
    >
      <T context="pivot stale result">
        This pivot or its data changed since this run. Run again to reflect your
        changes.
      </T>
    </p>
  );
}
